import { useEffect, useRef, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, ListItemIcon, ListItemText, Menu, MenuItem, Popover, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DescriptionIcon from "@mui/icons-material/Description";
import DataObjectIcon from "@mui/icons-material/DataObject";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import API from "../api";
import AssetCustomFilters from "./AssetCustomFilters";
import {
  ColumnsButton, FilterButton, GridToolbarBar, ToolbarDivider, ToolbarGroup,
  ToolbarSpacer, ToolIconButton, ToolTextButton,
} from "./ToolbarKit";
import { colors } from "../theme";
import { startToast, updateToastError, updateToastSuccess } from "../utils/notification-loading";
import { useOrion } from "../contexts/OrionContext";

// Export asset -- SOLO questi campi (richiesta esplicita: gli export/API
// davano troppi dati, che restano nella dashboard ma non devono finire in un
// file scaricato). "operations" (catalogo operazioni OT osservate, TASK-AI
// Antlia) si aggiunge SOLO se il modulo OT è attivo -- altrimenti l'array è
// sempre vuoto e sarebbe solo rumore. Stessi campi per CSV (appiattiti in
// stringa) e JSON (struttura), niente più "JSON completo" col documento intero
// (allineato a on-prem).
function summarizeServices(services) {
  return (services || [])
    .map((s) => `${s.port ?? "?"}${s.protocol ? `/${s.protocol.toLowerCase()}` : ""}${s.name ? ` (${s.name})` : ""}`)
    .join("; ");
}
function basicServices(services) {
  return (services || []).map((s) => ({ port: s.port ?? null, protocol: s.protocol ?? null, name: s.name ?? null }));
}

function summarizeIpChanges(history) {
  return (history || [])
    .map((h) => `${h.old_ip || "?"} → ${h.new_ip || "?"}${h.changed_at ? ` @ ${dayjs(h.changed_at).format("YYYY-MM-DD HH:mm")}` : ""}`)
    .join("; ");
}
function basicIpChanges(history) {
  return (history || []).map((h) => ({ old_ip: h.old_ip ?? null, new_ip: h.new_ip ?? null, changed_at: h.changed_at ?? null }));
}

function summarizeOperations(operations) {
  return (operations || [])
    .map((o) => `${o.protocol || ""}${o.operation ? `:${o.operation}` : ""}${o.is_write ? " (write)" : ""}`)
    .join("; ");
}
function basicOperations(operations) {
  return (operations || []).map((o) => ({ protocol: o.protocol ?? null, operation: o.operation ?? null, is_write: o.is_write ?? null }));
}

function csvFieldsFor(otEnabled) {
  const fields = [
    { header: "IP", get: (r) => r.ip },
    { header: "MAC", get: (r) => r.mac },
    { header: "Vendor", get: (r) => r.vendor },
    { header: "Status", get: (r) => r.status },
    { header: "VLAN", get: (r) => r.vlan ?? "" },
    { header: "Services", get: (r) => summarizeServices(r.services) },
    { header: "IP Changes", get: (r) => summarizeIpChanges(r.ip_history) },
  ];
  if (otEnabled) fields.push({ header: "Operations", get: (r) => summarizeOperations(r.operations) });
  return fields;
}

function basicRecord(r, otEnabled) {
  const rec = {
    ip: r.ip || "",
    mac: r.mac || "",
    vendor: r.vendor || "",
    status: r.status || "",
    vlan: r.vlan ?? null,
    services: basicServices(r.services),
    ip_changes: basicIpChanges(r.ip_history),
  };
  if (otEnabled) rec.operations = basicOperations(r.operations);
  return rec;
}

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Stessa distinzione reseller/user già usata altrove (network-status/overview
// e status/index.jsx) -- senza questo i bottoni sotto navigavano sempre verso
// /user/... anche quando il toolbar è montato dentro /reseller/myC/... (un
// reseller che ispeziona l'inventory di un cliente), portando a un 404.
const IS_RESELLER = window.sessionStorage.getItem("type") === "reseller";
const ZONES_PATH = IS_RESELLER ? "/reseller/myC/network-status/zones" : "/user/network-status/zones";
const LECS_MANAGEMENT_PATH = IS_RESELLER ? "/reseller/myC/lecs-management" : "/user/lecs-management";

function downloadFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Riflette normalizeImportRow lato backend (assets-ctrl.mjs) -- se cambiano
// i campi letti dall'import lì, questa lista va aggiornata di pari passo.
const IMPORT_FIELDS = [
  { name: "ip", required: true, desc: "IPv4 address" },
  { name: "mac", required: true, desc: "MAC address" },
  { name: "vendor", required: false, desc: "Device manufacturer" },
  { name: "machine_name", required: false, desc: "Custom/friendly name" },
  { name: "os_family", required: false, desc: "e.g. Windows, Linux" },
  { name: "os_version", required: false, desc: "e.g. Windows 11 Pro" },
  { name: "os_build", required: false, desc: "e.g. 10.0.26100" },
  { name: "os_release", required: false, desc: "OS release/edition" },
  { name: "asset_type", required: false, desc: "it | ot | iot | iomt" },
  { name: "risk", required: false, desc: "Numeric risk score" },
];

const CSV_EXAMPLE = `ip,mac,vendor,machine_name,os_family,os_version,os_build,os_release,asset_type,risk
192.168.1.10,AA:BB:CC:DD:EE:FF,Siemens,PLC-Line1,Windows,Windows 11 Pro,10.0.26100,,ot,45`;

const JSON_EXAMPLE = `[
  {
    "ip": "192.168.1.10",
    "mac": "AA:BB:CC:DD:EE:FF",
    "vendor": "Siemens",
    "machine_name": "PLC-Line1",
    "os_family": "Windows",
    "asset_type": "ot",
    "risk": 45
  }
]`;

const AssetToolBar = ({ filterModel, setFilterModel, onImportDone, rows = [] }) => {
  const [anchor, setAnchor] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);
  const navigate = useNavigate();
  const { orionEnabled } = useOrion();
  const importInputRef = useRef(null);
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [otEnabled, setOtEnabled] = useState(false);

  useEffect(() => {
    API.getLicense()
      .then((res) => {
        const ot = (res.data?.modules ?? []).find((m) => m.name === "ot");
        setOtEnabled(ot ? !!ot.enabled : false);
      })
      .catch(() => {});
  }, []);

  const timestamp = () => dayjs().format("YYYYMMDD-HHmmss");

  // CSV: IP, MAC, vendor, status, VLAN, servizi esposti, storico cambi IP
  // (+ operations OT se Antlia è attivo).
  const handleExportCsv = () => {
    setExportAnchor(null);
    const fields = csvFieldsFor(otEnabled);
    const header = fields.map((f) => csvEscape(f.header)).join(",");
    const lines = rows.map((r) => fields.map((f) => csvEscape(f.get(r))).join(","));
    downloadFile(`asset-inventory-${timestamp()}.csv`, [header, ...lines].join("\r\n"), "text/csv;charset=utf-8;");
  };

  // JSON: stessi campi del CSV, in forma strutturata invece che appiattita
  // in stringa -- non più il documento asset intero.
  const handleExportJson = () => {
    setExportAnchor(null);
    const data = rows.map((r) => basicRecord(r, otEnabled));
    downloadFile(`asset-inventory-${timestamp()}.json`, JSON.stringify(data, null, 2), "application/json;charset=utf-8;");
  };

  const handleProceedToFilePicker = () => {
    setFormatDialogOpen(false);
    importInputRef.current?.click();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permette di ricaricare lo stesso file due volte di fila
    if (!file) return;
    setPendingFile(file);
  };

  const handleCancelImport = () => setPendingFile(null);

  const handleConfirmImport = () => {
    const file = pendingFile;
    setPendingFile(null);
    if (!file) return;

    startToast();
    API.importAssets(file)
      .then((res) => {
        if (res.data.success) {
          updateToastSuccess(res.data.message || "Import completed successfully");
          onImportDone?.();
        } else {
          updateToastError(res.data.error || "Import failed");
        }
      })
      .catch((err) => updateToastError(err.response?.data?.error || "Import failed"));
  };

  return (
    <GridToolbarBar>
      <ToolbarGroup>
        <ColumnsButton />
        <FilterButton count={filterModel.length} onClick={(e) => setAnchor(e.currentTarget)} />
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolIconButton title="Inventory configuration" onClick={() => navigate(`${LECS_MANAGEMENT_PATH}?tab=2&section=asset_inventory`)}>
          <SettingsEthernetIcon fontSize="small" />
        </ToolIconButton>
        <ToolIconButton
          title={orionEnabled ? "Zones" : "Zones (Orion module is disabled)"}
          disabled={!orionEnabled}
          onClick={() => navigate(ZONES_PATH)}
        >
          <PlaceOutlinedIcon fontSize="small" />
        </ToolIconButton>
      </ToolbarGroup>

      <ToolbarSpacer />

      <ToolTextButton icon={<FileUploadIcon fontSize="small" />} onClick={() => setFormatDialogOpen(true)}>
        Import
      </ToolTextButton>
      <input
        ref={importInputRef}
        type="file"
        accept=".csv,.json,application/json,text/csv"
        hidden
        onChange={handleFileSelected}
      />

      <ToolTextButton icon={<SaveAltIcon fontSize="small" />} onClick={(e) => setExportAnchor(e.currentTarget)}>
        Export
      </ToolTextButton>
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
        slotProps={{ paper: { sx: { backgroundColor: "#161616", border: `1px solid ${colors.grey[800]}` } } }}
      >
        <MenuItem onClick={handleExportCsv}>
          <ListItemIcon><InsertDriveFileIcon sx={{ fontSize: 18, color: colors.grey[400] }} /></ListItemIcon>
          <ListItemText
            primary="CSV"
            secondary={`IP, MAC, vendor, status, VLAN, services, IP changes${otEnabled ? ", operations" : ""}`}
            slotProps={{ secondary: { sx: { fontSize: 11, color: colors.grey[500] } } }}
          />
        </MenuItem>
        <MenuItem onClick={handleExportJson}>
          <ListItemIcon><DataObjectIcon sx={{ fontSize: 18, color: colors.grey[400] }} /></ListItemIcon>
          <ListItemText
            primary="JSON"
            secondary={`Same fields as CSV, structured${otEnabled ? " (incl. operations)" : ""}`}
            slotProps={{ secondary: { sx: { fontSize: 11, color: colors.grey[500] } } }}
          />
        </MenuItem>
      </Menu>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 1 } } }}
      >
        <AssetCustomFilters filterModel={filterModel} setFilterModel={setFilterModel} />
      </Popover>

      <Dialog
        open={formatDialogOpen}
        onClose={() => setFormatDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "#161616", backgroundImage: "none" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, borderBottom: `1px solid ${colors.grey[800]}`, pb: 1.5 }}>
          <DescriptionIcon sx={{ color: "#459095", fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.grey[100] }}>Import file format</Typography>
            <Typography sx={{ fontSize: 12, color: colors.grey[400], mt: 0.2 }}>
              CSV (with header row) or JSON (array of objects) — one row/object per asset.
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "#161616", pt: 3 }}>
          <Box sx={{
            p: 1.2, mb: 2, borderRadius: 1.5,
            border: "1px solid #459095", backgroundColor: "#45909518",
          }}>
            <Typography sx={{ fontSize: 12, color: colors.grey[100] }}>
              Every row must have at least one of <b>ip</b> or <b>mac</b> — rows missing both are skipped, the rest of the file is still imported.
            </Typography>
          </Box>

          <Table size="small" sx={{ "& td, & th": { borderColor: colors.grey[800], fontSize: 12.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: colors.grey[500] }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 700, color: colors.grey[500] }}>Required</TableCell>
                <TableCell sx={{ fontWeight: 700, color: colors.grey[500] }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {IMPORT_FIELDS.map((f) => (
                <TableRow key={f.name}>
                  <TableCell sx={{ fontFamily: "monospace", color: colors.grey[100] }}>{f.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={f.required ? "ip or mac" : "Optional"}
                      size="small"
                      sx={{
                        fontSize: 10, fontWeight: 700, height: 20,
                        bgcolor: f.required ? "#45909522" : `${colors.grey[700]}55`,
                        color: f.required ? "#459095" : colors.grey[400],
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: colors.grey[400] }}>{f.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Divider sx={{ my: 2, borderColor: `${colors.grey[700]}55` }} />

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.grey[500], textTransform: "uppercase", letterSpacing: 0.6, mb: 0.8 }}>
            CSV example
          </Typography>
          <Box component="pre" sx={{
            m: 0, mb: 2, p: 1.2, borderRadius: 1, overflowX: "auto",
            backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`,
            fontSize: 11, color: colors.grey[300],
          }}>
            {CSV_EXAMPLE}
          </Box>

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.grey[500], textTransform: "uppercase", letterSpacing: 0.6, mb: 0.8 }}>
            JSON example
          </Typography>
          <Box component="pre" sx={{
            m: 0, p: 1.2, borderRadius: 1, overflowX: "auto",
            backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`,
            fontSize: 11, color: colors.grey[300],
          }}>
            {JSON_EXAMPLE}
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#161616", borderTop: `1px solid ${colors.grey[800]}` }}>
          <Button onClick={() => setFormatDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={handleProceedToFilePicker}>
            Choose file
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingFile)}
        onClose={handleCancelImport}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "#161616", backgroundImage: "none" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, borderBottom: `1px solid ${colors.grey[800]}`, pb: 1.5 }}>
          <FileUploadIcon sx={{ color: "#459095", fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.grey[100] }}>Import assets</Typography>
            <Typography sx={{ fontSize: 12, color: colors.grey[400], mt: 0.2 }}>
              Every row in the file is added as a new asset in Inventory.
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "#161616", pt: 3 }}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.2, p: 1.2,
            borderRadius: 1.5, border: `1px solid ${colors.grey[800]}`, backgroundColor: "#1c1c1c",
          }}>
            <InsertDriveFileIcon sx={{ color: colors.grey[500], fontSize: 22, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.grey[100] }} noWrap>
                {pendingFile?.name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: colors.grey[500] }}>
                {pendingFile ? `${(pendingFile.size / 1024).toFixed(1)} KB` : ""}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#161616", borderTop: `1px solid ${colors.grey[800]}` }}>
          <Button onClick={handleCancelImport}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={handleConfirmImport}>
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </GridToolbarBar>
  );
};

export default AssetToolBar;
